// =============================================================================
// JUGO Quiz — Scoring & Bundle Engine
//
// Three responsibilities:
//   1. Score products from quiz answers
//   2. Select the bundle (with mandatory minimums + family logic)
//   3. Compute the BYOB × Subscribe × STACK20 price cascade
// =============================================================================

// Real per-bottle pricing — mirrors the live BYOB builder (jugo-byob-v2.js)
// EXACTLY, because the bundle-tier discount is an automatic cart discount the
// checkout actually applies. Two real discounts stack multiplicatively:
//   Bundle tier (auto):  2–3 jars 10%  ·  4–5 jars 12.5%  ·  6 jars 15%
//   Subscription:        flat 15%
// e.g. 3 jars subscribing = (1-0.10)(1-0.15) = 23.5% off = $15.29/bottle,
// which is precisely what the cart rings up. Capped well under the 28% rule.
const MSRP = 19.99;
const SUB_DISCOUNT = 0.15;
const BUNDLE_DISCOUNT = { 1: 0, 2: 0.10, 3: 0.10, 4: 0.125, 5: 0.125, 6: 0.15 };
function bundleTierPct(n) { return n <= 1 ? 0 : (BUNDLE_DISCOUNT[n] != null ? BUNDLE_DISCOUNT[n] : 0.15); }

// Real Shopify variant + subscription selling-plan IDs, so the result page
// can actually add the recommended regimen to the cart.
// 2333016124 = "4 Week Subscription" — the ONLY plan sold to new customers.
// The 2wk/3wk/monthly plans exist solely to grandfather legacy subscribers
// and must never be sent to cart from the site.
const PRODUCT_VARIANTS = {
  energy:     { variant: 40588972687420, sellingPlan: 2333016124 },
  focus:      { variant: 40589076758588, sellingPlan: 2333016124 },
  beauty:     { variant: 40589046022204, sellingPlan: 2333016124 },
  acv:        { variant: 40589064994876, sellingPlan: 2333016124 },
  kids_daily: { variant: 43522083323964, sellingPlan: 2333016124 },
  kids_sport: { variant: 43522087354428, sellingPlan: 2333016124 },
};

// -----------------------------------------------------------------------------
// 1. SCORING
// Walks all answers, applies score deltas to each product.
// Returns { product_id: numeric_score }, sorted desc not required.
// -----------------------------------------------------------------------------

function scoreProducts(answers) {
  const scores = { energy: 0, focus: 0, beauty: 0, acv: 0, kids_daily: 0, kids_sport: 0 };

  for (const question of QUESTIONS) {
    const answer = answers[question.id];
    if (answer === undefined || answer === null) continue;

    const selectedValues = question.type === 'multi'
      ? (Array.isArray(answer) ? answer : [])
      : [answer];

    for (const value of selectedValues) {
      const option = question.options.find(o => o.value === value);
      if (!option || !option.score) continue;

      for (const [pid, points] of Object.entries(option.score)) {
        scores[pid] = (scores[pid] || 0) + points;
      }
    }
  }

  return scores;
}

// -----------------------------------------------------------------------------
// 2. BUNDLE SELECTION
// Rules (from the strategy doc):
//  - Adult path: minimum 3 bottles (Most Popular tier)
//  - Family path: target 6 bottles (Complete Family — the anchor offer)
//  - "Maximalist" tag in Q6 → force the full stack for their path
//  - Never under 3. Never recommend just one product.
// -----------------------------------------------------------------------------

function selectBundle(scores, answers) {
  const audience = answers.audience;
  const maximalist = Array.isArray(answers.future) && answers.future.includes('all_future');
  const primary = answers.primary_adult || 'energy';

  const adultIds = ['energy', 'focus', 'beauty', 'acv'];
  const kidsIds  = ['kids_daily', 'kids_sport'];

  // A product is "endorsed" if its score is meaningful — primary forces 1000,
  // explicit secondary picks (Q3 anchors, Q4 multi-selects) score 40+. A score
  // under 30 is incidental signal only and shouldn't pull the SKU into the
  // bundle unless we need fillers.
  const ENDORSEMENT_THRESHOLD = 40;

  const endorsedAdult = adultIds
    .filter(id => scores[id] >= ENDORSEMENT_THRESHOLD)
    .sort((a, b) => scores[b] - scores[a]);

  const endorsedKids = kidsIds
    .filter(id => scores[id] >= ENDORSEMENT_THRESHOLD)
    .sort((a, b) => scores[b] - scores[a]);

  // ---- Target bundle sizes per audience ----
  // Hardcoded to the strategy doc: adult min 3 (Most Popular), family always 6
  // (Complete Family = 4 adult + 2 kids), maximalist signal bumps adult to 4.
  let targetAdult, targetKids;
  if (audience === 'self' || audience === 'couple') {
    targetAdult = maximalist ? 4 : 3;
    targetKids = 0;
  } else if (audience === 'parent') {
    targetAdult = maximalist ? 4 : 3;
    targetKids = Math.max(1, endorsedKids.length); // respect what they endorsed
  } else if (audience === 'family') {
    targetAdult = 4;
    targetKids = 2;                                // forces Complete Family Stack
  } else {
    targetAdult = 3;
    targetKids = 0;
  }

  // ---- Round up adult picks using narrative fill order (not score) ----
  // We start with what the user endorsed, then fill with the affinity-ordered
  // list from FILL_ORDER[primary]. This makes the 3-bottle minimum feel like
  // a coherent regimen, not a random padding-to-quota.
  const adultPicks = [...endorsedAdult];
  const fillSeq = FILL_ORDER[primary] || FILL_ORDER.energy;
  for (const candidate of fillSeq) {
    if (adultPicks.length >= targetAdult) break;
    if (!adultPicks.includes(candidate)) adultPicks.push(candidate);
  }

  // ---- Round up kids picks (only two SKUs, so order is trivial) ----
  const kidsPicks = [...endorsedKids];
  if (targetKids > 0) {
    for (const candidate of kidsIds) {
      if (kidsPicks.length >= targetKids) break;
      if (!kidsPicks.includes(candidate)) kidsPicks.push(candidate);
    }
  }

  return [...adultPicks, ...kidsPicks];
}

// -----------------------------------------------------------------------------
// 3. PRICE CASCADE
// BYOB × Subscribe (0.85) × STACK20 (0.80)
// -----------------------------------------------------------------------------

function computePricing(bundle) {
  const bundleSize = bundle.length;
  const adultCount = bundle.filter(id => PRODUCTS[id]?.audience === 'adult').length;
  const kidsCount  = bundle.filter(id => PRODUCTS[id]?.audience === 'kids').length;

  // Multiplicative stack, matching the cart: bundle tier × subscription.
  const stackMultiplier = (1 - bundleTierPct(bundleSize)) * (1 - SUB_DISCOUNT);
  // Ceil to the cent — Shopify rounds the discounted per-line price up, so this
  // makes the quoted price match the cart exactly (never quote BELOW the cart).
  const perBottleFinal = Math.ceil(MSRP * stackMultiplier * 100) / 100;
  const finalTotal = Math.round(perBottleFinal * bundleSize * 100) / 100;
  const msrpTotal = bundleSize * MSRP;
  const totalSavingsPct = 1 - stackMultiplier; // 15% → 27.75% by tier

  // Composition-aware badge.
  // Voice: fun, lifestyle, beachbum — anchored by serious ingredient quality.
  // The badge names treat kids as sidekicks, not obligations, and lean into
  // the "we're all on this trip" frame instead of corporate "stack" language.
  let badge = null;
  if (bundleSize === 3) {
    badge = 'Fan Favorite';
  } else if (bundleSize === 4) {
    if (adultCount === 4)                       badge = 'All In';
    else if (adultCount === 3 && kidsCount === 1) badge = 'You + Sidekick';
    else if (adultCount === 2 && kidsCount === 2) badge = 'You + Sidekicks';
  } else if (bundleSize === 6 && adultCount === 4 && kidsCount === 2) {
    badge = 'The Whole Crew';
  }
  // Sizes 1, 2, 5, and unrecognized 4/6 compositions intentionally fall
  // through with no badge — the worst outcome is no badge, not a wrong one.

  // Free-gift unlocks by jar count — mirrors the live BYOB builder
  // (freshener at 2, tote at 4, hat at 6).
  const freeFreshener = bundleSize >= 2;
  const freeTote      = bundleSize >= 4;
  const freeHat       = bundleSize >= 6;
  let nextUnlock = null;
  if (bundleSize < 2)      nextUnlock = { reward: 'a free air freshener', need: 2 - bundleSize };
  else if (bundleSize < 4) nextUnlock = { reward: 'a free JUGO tote',     need: 4 - bundleSize };
  else if (bundleSize < 6) nextUnlock = { reward: 'a free JUGO hat',      need: 6 - bundleSize };

  return {
    bundleSize,
    adultCount,
    kidsCount,
    badge,
    msrpTotal,
    finalTotal,
    perBottleMsrp: MSRP,
    perBottleFinal,
    totalSavingsPct,
    freeShipping: bundleSize >= 2,
    freeFreshener,
    freeTote,
    freeHat,
    nextUnlock,
  };
}

// -----------------------------------------------------------------------------
// 4. PAIN COLLECTION
// Walk answers to extract every pain tag the user surfaced. Used to pick the
// right role_for[pain] sentence for each product card.
// -----------------------------------------------------------------------------

function collectPains(answers) {
  const pains = new Set();
  for (const question of QUESTIONS) {
    const answer = answers[question.id];
    if (answer === undefined || answer === null) continue;
    const selected = question.type === 'multi'
      ? (Array.isArray(answer) ? answer : [])
      : [answer];
    for (const value of selected) {
      const opt = question.options.find(o => o.value === value);
      if (opt && opt.pains) opt.pains.forEach(p => pains.add(p));
    }
  }
  return pains;
}

// -----------------------------------------------------------------------------
// 5. ROLE SENTENCE PICKER
// For each product in the bundle, pick the role sentence that matches the
// user's strongest pain — fall back to default_role.
// -----------------------------------------------------------------------------

function pickRoleSentence(productId, painsSet) {
  const product = PRODUCTS[productId];
  if (!product.role_for) return product.default_role;
  for (const pain of painsSet) {
    if (product.role_for[pain]) return product.role_for[pain];
  }
  return product.default_role;
}

// -----------------------------------------------------------------------------
// 5b. DYNAMIC COPY BUILDERS (bundle-aware)
//
// These replace the static OBJECTION_REVERSALS.fresh / .nothing / .unclear
// and the four WHY_STACK templates. The reason: those static strings named
// specific ingredients (turmeric, biotin, ACV, etc.) and specific layers
// (slow-burn skin/hair) that may or may not be in the user's bundle. A
// result page that quotes a product the user isn't getting is a trust
// killer. Each builder pulls only from products actually in the bundle.
// -----------------------------------------------------------------------------

function joinNL(list) {
  if (!list || list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

// "Fresh start" reversal — composes the superfoods list and layer language
// from products actually in the bundle. Never names turmeric unless Beauty
// is in the bundle, never claims a slow-burn layer unless Beauty is there.
function buildFreshReversal(bundle) {
  const ingredients = [];
  for (const pid of bundle) {
    const ing = PRODUCTS[pid].signature_ingredient;
    if (ing && !ingredients.includes(ing)) ingredients.push(ing);
  }

  const layersByType = { responsive: [], 'slow-burn': [], kids: [] };
  for (const pid of bundle) {
    const layer = PRODUCTS[pid].layer;
    if (layer && layersByType[layer.type]) layersByType[layer.type].push(layer.name);
  }

  const parts = [];
  if (layersByType.responsive.length > 0) {
    parts.push(`the responsive layers up front (${joinNL(layersByType.responsive)})`);
  }
  if (layersByType['slow-burn'].length > 0) {
    parts.push(`the slow-burn layer (${joinNL(layersByType['slow-burn'])}) running in the background`);
  }
  if (layersByType.kids.length > 0) {
    parts.push("the kids' layer covered alongside");
  }
  const layerSentence = joinNL(parts);

  return `**Fresh start — the best kind.** Nothing to unlearn, just good habits to build. The regimen below is built from real superfoods (${joinNL(ingredients)}), structured the way your biology actually works — ${layerSentence}.`;
}

// "Didn't feel anything" reversal — only names doses for products in the bundle.
function buildNothingReversal(bundle) {
  const doses = [];
  for (const pid of bundle) {
    const dose = PRODUCTS[pid].hero_dose;
    if (dose) doses.push(dose);
  }
  const top = doses.slice(0, 3); // cap at 3 — keeps the line readable

  if (top.length === 0) {
    return "**This time you'll feel the difference.** Most gummies cap at 100% DV with synthetic isolates — easy to miss. JUGO doses are sourced from real superfoods at clinical amounts. These are doses you can actually feel.";
  }

  return `**This time you'll feel the difference.** Most gummies cap at 100% DV with synthetic isolates — easy to miss. JUGO doses are ${joinNL(top)}, all sourced from real superfoods. These are doses you can actually feel.`;
}

// "Couldn't tell if they worked" — only names biology examples relevant to the bundle.
function buildUnclearReversal(bundle) {
  const examples = [];
  if (bundle.includes('beauty')) {
    examples.push('Hair runs on a 90-day cycle. Skin turns over every 60.');
  }
  if (bundle.includes('focus') || bundle.includes('energy')) {
    examples.push('Mood and energy are the earliest to shift.');
  }
  if (bundle.includes('acv')) {
    examples.push('Gut and metabolism build over weeks, not days.');
  }
  const joined = examples.join(' ');

  return `**This time you'll see it working.** Most people take one bottle for three weeks and stop, right before the good part. ${joined} The regimen below is built to carry you all the way through that arc.`;
}

// Why-a-regimen panel — composes axes and ingredient list from the actual
// secondary products in the bundle. Each primary outcome has its own voice.
function buildWhyStack(primary, bundle) {
  const others = bundle.filter(pid => pid !== primary && PRODUCTS[pid].audience === 'adult');

  // Build the list of "why one bottle isn't enough" axes from secondary products
  const axes = others
    .map(pid => PRODUCTS[pid].axis_label)
    .filter(Boolean);

  // Build the ingredient backing list (deduped)
  const ingredients = [];
  for (const pid of bundle) {
    for (const ing of (PRODUCTS[pid].why_ingredients || [])) {
      if (!ingredients.includes(ing)) ingredients.push(ing);
    }
  }

  // Per-primary lead-in voice (kept close to the original copy)
  if (primary === 'energy') {
    if (axes.length === 0) {
      return `Energy is the floor — and this holds it with real superfoods at clinical doses (${joinNL(ingredients)}), the real thing rather than synthetic isolates dressed up as gummies.`;
    }
    const numWord = axes.length + 1 === 2 ? 'both' : `all ${axes.length + 1}`;
    return `Energy gets you up — and energy that lasts leans on more than one layer: ${joinNL(axes)}. That's where a regimen does what a single bottle can't. It supports ${numWord} at once — with real superfoods at clinical doses (${joinNL(ingredients)}), the real thing, not synthetic isolates dressed up as gummies.`;
  }

  if (primary === 'focus') {
    const partners = [];
    if (bundle.includes('energy')) partners.push('B vitamins from organic greens');
    if (bundle.includes('acv'))    partners.push('the gut layer that drives B12 absorption');
    if (bundle.includes('beauty')) partners.push('the recovery layer that flourishes once stress settles');
    if (partners.length === 0) {
      return `Focus is the calm-and-clear layer for a busy mind. Real ashwagandha root extract — the OG adaptogen, 3,000 years older than the supplement industry — at the clinical 300mg dose. The real thing, not a sprinkle on the label.`;
    }
    return `Focus calms the head beautifully — and a calm head feels even better with fuel behind it. The regimen pairs real ashwagandha root extract (the OG adaptogen — 3,000 years older than the supplement industry) with ${joinNL(partners)}. Calm AND fueled is the real unlock.`;
  }

  if (primary === 'beauty') {
    const upstream = [];
    if (bundle.includes('acv'))    upstream.push('gut');
    if (bundle.includes('focus'))  upstream.push('calm');
    if (bundle.includes('energy')) upstream.push('vitality');
    const upstreamSentence = upstream.length > 0
      ? `the upstream layer (${upstream.join(' + ')}) — because calm, low-inflammation days are when hair and skin look their best`
      : 'a clear runway of consistency, because the visible layer rewards patience most';
    return `Hair grows in 3 cycles a year; skin turns over every 28 days. Thirty days is one biological cycle — a great start, with the best still ahead. The regimen pairs the visible layer (biotin, collagen, hyaluronic acid, turmeric root) with ${upstreamSentence}. Real superfoods doing the work the way nature already worked it out.`;
  }

  if (primary === 'acv') {
    const downstream = [];
    if (bundle.includes('energy')) downstream.push('metabolism (B12)');
    if (bundle.includes('focus'))  downstream.push('a calm inflammation response (superfruit antioxidants — acai, pomegranate, goji)');
    if (bundle.includes('focus'))  downstream.push('steady stress levels (cortisol shapes where weight sits)');
    if (bundle.includes('beauty')) downstream.push('the glow that follows downstream (skin and hair)');
    if (downstream.length === 0) {
      return `ACV is the upstream lever that quietly lifts everything else. 500mg of real apple cider vinegar — the active dose that does the work, not the cosmetic one. Real superfoods doing the real work, not synthetic flavor in a gummy.`;
    }
    return `ACV brings that light, flat feeling — and it goes further when you support what feeds into it: ${joinNL(downstream)}. The regimen supports the gut from every angle, all from real food sources — that's why it keeps delivering where one bottle stalls.`;
  }

  // Fallback (should never hit)
  return `The regimen below is built from real superfoods (${joinNL(ingredients)}), at the doses that actually do the work.`;
}

// -----------------------------------------------------------------------------
// 6. DYNAMIC HEADLINE
// The headline ALWAYS anchors on the Q2 primary outcome — that's the user's
// stated #1, and the regimen name is built from the same input, so anchoring
// here keeps the headline and the regimen name telling one coherent story.
// Q6 future-pacing outcomes are then folded in on top. (Earlier this was
// pure-Q6, which let the headline contradict the regimen name when a user's
// Q2 and Q6 answers diverged.)
// -----------------------------------------------------------------------------

// Short, comma-free phrases for the multi-outcome headline join. Comma-free
// matters: these get comma-separated in a list, so a phrase with its own
// comma turns the headline into mush.
const FUTURE_PHRASES = {
  energy_future: 'steady all-day energy',
  calm_future:   'a clear head',
  glow_future:   'hair and skin people notice',
  flat_future:   'a flatter stomach',
};

const SINGLE_FUTURE_HEADLINES = {
  energy_future: "Steady, all-day energy — and the regimen that holds it.",
  calm_future:   "A calm, sharp head — and the regimen that keeps it.",
  glow_future:   "Hair and skin people notice — and the regimen that earns it.",
  flat_future:   "A flat, easy stomach — and the regimen that keeps it there.",
};

// Q2 primary outcome → its matching Q6 future-key, so the headline can
// always lead with the user's stated #1 priority.
const PRIMARY_TO_FUTURE_KEY = {
  energy: 'energy_future',
  focus:  'calm_future',
  beauty: 'glow_future',
  acv:    'flat_future',
};

// Q6 future-key → outcome theme. Used both for the headline and to keep the
// callback from re-surfacing a concern the headline already addresses.
const FUTURE_TO_THEME = {
  energy_future: 'energy',
  calm_future:   'focus',
  glow_future:   'beauty',
  flat_future:   'acv',
};

function buildHeadline(answers, primary) {
  const future = Array.isArray(answers.future) ? answers.future : [];

  // Maximalist signal — the user explicitly said "all of it"
  if (future.includes('all_future')) {
    return "All of it — energy, calm, glow, gut. The full regimen, no half-measures.";
  }

  // Anchor on the primary outcome (Q2), then fold in extra Q6 outcomes.
  // The primary key is ALWAYS first in the list — that's the coherence lock.
  const primaryKey = PRIMARY_TO_FUTURE_KEY[primary] || 'energy_future';
  const keys = [primaryKey];
  for (const pick of future) {
    if (FUTURE_PHRASES[pick] && !keys.includes(pick)) keys.push(pick);
  }

  if (keys.length === 1) {
    return SINGLE_FUTURE_HEADLINES[keys[0]];
  }
  const phrases = keys.map(k => FUTURE_PHRASES[k]);
  // 2 outcomes → comma join (the phrases can contain "and" themselves, so a
  // comma reads cleaner than stacking another "and"). 3+ → Oxford-comma list.
  const joined = phrases.length === 2
    ? `${phrases[0]}, ${phrases[1]}`
    : `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
  const cap = joined[0].toUpperCase() + joined.slice(1);
  return `${cap} — the regimen that gets you there.`;
}

// The set of outcome themes the headline covers (primary + any Q6 picks).
// The callback uses this to avoid re-surfacing a concern already in the headline.
function headlineThemes(answers, primary) {
  const future = Array.isArray(answers.future) ? answers.future : [];
  if (future.includes('all_future')) {
    return new Set(['energy', 'focus', 'beauty', 'acv']);
  }
  const themes = new Set([primary]); // primary is always in the headline
  for (const pick of future) {
    if (FUTURE_TO_THEME[pick]) themes.add(FUTURE_TO_THEME[pick]);
  }
  return themes;
}

// -----------------------------------------------------------------------------
// 7. ASSEMBLE THE FULL RESULT
// Returns everything the result view needs.
// -----------------------------------------------------------------------------

function buildResult(answers) {
  const scores = scoreProducts(answers);
  const bundle = selectBundle(scores, answers);
  const pricing = computePricing(bundle);
  const pains = collectPains(answers);

  const primary = answers.primary_adult || 'energy';
  const audience = answers.audience;
  const isFamily = audience === 'parent' || audience === 'family';
  const regimenName = REGIMEN_NAMES[primary]?.[isFamily ? 'family' : 'adult']
    || REGIMEN_NAMES[primary]?.adult
    || 'Your Daily Regimen';

  const headline = buildHeadline(answers, primary);

  // Dynamic, bundle-aware versions of the why-stack and the three reversals
  // that named specific ingredients/layers. Static reversals stay for the
  // simple cases (forgot, taste, pills) where bundle composition doesn't
  // affect accuracy.
  const whyStack = buildWhyStack(primary, bundle);
  let objectionReversal;
  if (answers.objection === 'fresh')        objectionReversal = buildFreshReversal(bundle);
  else if (answers.objection === 'nothing') objectionReversal = buildNothingReversal(bundle);
  else if (answers.objection === 'unclear') objectionReversal = buildUnclearReversal(bundle);
  else objectionReversal = OBJECTION_REVERSALS[answers.objection] || OBJECTION_REVERSALS.forgot;

  const commitTag = (() => {
    const opt = QUESTIONS.find(q => q.id === 'commit')?.options.find(o => o.value === answers.commit);
    return opt?.tag || 'committed';
  })();
  const commitFrame = COMMIT_FRAMES[commitTag];

  // Build product cards with role sentences
  const productCards = bundle.map(pid => ({
    ...PRODUCTS[pid],
    role: pickRoleSentence(pid, pains),
    variant: PRODUCT_VARIANTS[pid] ? PRODUCT_VARIANTS[pid].variant : null,
    sellingPlan: PRODUCT_VARIANTS[pid] ? PRODUCT_VARIANTS[pid].sellingPlan : null,
  }));

  // "You also said..." callback — proves the regimen isn't templated.
  // Draws from Q4 (what else), skipping anything the headline already
  // covers, so the callback always adds NEW information, never an echo.
  const callback = buildCallback(answers, primary);

  // Kids acknowledgment line — shown only when the bundle includes kids SKUs.
  const kidsLine = buildKidsLine(bundle);

  return {
    regimenName,
    headline,
    callback,
    kidsLine,
    whyStack,
    objectionReversal,
    commitFrame,
    productCards,
    pricing,
    primary,
    audience,
    isFamily,
    scores, // debug
    pains: [...pains], // debug
  };
}

// Q4 concern → its outcome theme. Lets the callback skip anything the
// headline already covers, so the result page doesn't say "skin" twice.
const Q4_THEME = {
  sleep: 'focus', bloat: 'acv', hair: 'beauty', skin: 'beauty',
  brain_fog: 'focus', edge: 'focus', weight: 'acv', caffeine: 'energy',
};

function buildCallback(answers, primary) {
  const themes = headlineThemes(answers, primary);

  // Q4 picks, minus 'none', minus anything whose theme is already in the
  // headline — the callback's job is to surface what the headline DOESN'T.
  const q4Picks = (Array.isArray(answers.else) ? answers.else : [])
    .filter(v => v !== 'none' && Q4_PHRASES[v])
    .filter(v => !themes.has(Q4_THEME[v]));

  if (q4Picks.length > 0) {
    const phrases = q4Picks.map(v => Q4_PHRASES[v]);
    const joined = phrases.length === 1
      ? phrases[0]
      : phrases.length === 2
        ? `${phrases[0]} and ${phrases[1]}`
        : `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
    return `You also told us you're after ${joined}. None of that is an afterthought here — it's exactly why the stack looks the way it does below.`;
  }

  // Q4 empty, or every Q4 pick is already in the headline — fall back to the
  // Tuesday read (always a different question than the headline draws from).
  const q3 = answers.tuesday;
  if (q3 && Q3_PHRASES[q3]) {
    return `You told us about your Tuesday 3pm — ${Q3_PHRASES[q3]}. The stack below is mapped straight to that, never picked from a template.`;
  }

  return '';
}

// A dedicated line acknowledging the kids' products. The headline and
// callback are adult-outcome-focused, so without this a family stack never
// mentions the kids in the copy zone above the product cards.
function buildKidsLine(bundle) {
  const kids = bundle.filter(pid => PRODUCTS[pid].audience === 'kids');
  if (kids.length === 0) return '';
  const names = kids.map(pid => PRODUCTS[pid].name.replace('JUGO ', ''));
  const verb = kids.length === 1 ? 'is' : 'are';
  return `Your kids are part of this too — ${joinNL(names)} ${verb} made just for them, not scaled-down adult doses.`;
}
